// Share a song into a conversation as a rich MUSIC card — search the connected catalog,
// share the currently-playing track, or paste a validated provider link. Posts via
// conversation-service (server re-validates the link). No listening data leaves the client;
// only the explicit share.
import React, { useEffect, useRef, useState } from 'react';
import { Play, X, Loader2, Search as SearchIcon, Music2 } from 'lucide-react';
import { Input } from '@/features/shared/components/ui/input';
import { Button } from '@/features/shared/components/ui/button';
import { isSafeProviderUrl } from '@/lib/providerLinks';
import { usePlayerSnapshot } from '@/features/music/player/usePlayer';
import { appleMusicProvider } from '@/features/music/lib/appleMusicProvider';
import type { MusicItem } from '@/features/music/lib/types';
import { conversationsApi } from '../api/conversationsApi';

const provider = appleMusicProvider();
const CONNECTED = new Set(['connected_eligible', 'connected_no_subscription']);

export const MusicShareComposer: React.FC<{ conversationId: string; onClose: () => void; onPosted?: () => void }> = ({ conversationId, onClose, onPosted }) => {
  const snap = usePlayerSnapshot();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  // Catalog search — available whenever Apple Music is connected.
  const [connected, setConnected] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<MusicItem[]>([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => { provider.getAuthorizationState().then((s) => setConnected(CONNECTED.has(s))).catch(() => setConnected(false)); }, []);
  useEffect(() => {
    window.clearTimeout(timer.current);
    const q = term.trim();
    if (!connected || !q) { setResults([]); setSearching(false); return; }
    setSearching(true);
    timer.current = window.setTimeout(async () => {
      try { const r = await provider.searchCatalog(q, ['songs'], { limit: 8 }); setResults(r.songs || []); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => window.clearTimeout(timer.current);
  }, [term, connected]);

  const post = async (input: { title: string; artist?: string; url: string; artworkUrl?: string }) => {
    if (!conversationId || busy) return;
    setBusy(true);
    try { await conversationsApi.shareMusic(conversationId, input); onPosted?.(); onClose(); }
    finally { setBusy(false); }
  };
  const shareSong = (i: MusicItem) => {
    if (!i.canonicalUrl) return;
    void post({ title: i.title, artist: i.artist, url: i.canonicalUrl, artworkUrl: provider.artworkUrl(i.artwork, 200) });
  };
  const shareCurrent = () => { if (snap.item?.canonicalUrl) shareSong(snap.item); };
  const shareLink = () => {
    const u = url.trim();
    if (!isSafeProviderUrl(u)) return;
    void post({ title: title.trim() || 'Shared a song', url: u });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Share a song</span>
        <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"><X className="h-4 w-4" /></button>
      </div>

      {/* Search the connected catalog. */}
      {connected && (
        <div className="mb-3">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search songs to share" aria-label="Search songs" className="pl-9" autoFocus />
            {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
          </div>
          {results.length > 0 && (
            <ul className="mt-1.5 max-h-56 space-y-0.5 overflow-y-auto rounded-lg border border-border p-1">
              {results.map((s) => {
                const art = provider.artworkUrl(s.artwork, 96);
                return (
                  <li key={s.id}>
                    <button type="button" disabled={busy} onClick={() => shareSong(s)}
                      className="flex w-full items-center gap-3 rounded-md p-1.5 text-left hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                      <div className="h-9 w-9 shrink-0 overflow-hidden rounded bg-muted">
                        {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Music2 className="h-4 w-4 text-muted-foreground" /></div>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground" dir="auto">{s.title}</div>
                        <div className="truncate text-xs text-muted-foreground" dir="auto">{s.artist}</div>
                      </div>
                      <span className="text-xs font-medium text-primary-500">Share</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {snap.item?.canonicalUrl && (
        <button type="button" disabled={busy} onClick={shareCurrent} className="mb-3 flex w-full items-center gap-3 rounded-lg border border-border p-2 text-left hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-muted"><Play className="h-4 w-4 text-primary-500" /></span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-foreground" dir="auto">Now playing: {snap.item.title}</div>
            <div className="truncate text-xs text-muted-foreground" dir="auto">{snap.item.artist}</div>
          </div>
          <span className="text-xs font-medium text-primary-500">Share</span>
        </button>
      )}

      <div className="space-y-2">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">or paste a link</div>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Apple Music, Spotify, or YouTube Music link" aria-label="Song or playlist link" />
        <div className="flex gap-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="flex-1" />
          <Button size="sm" disabled={busy || !isSafeProviderUrl(url.trim())} onClick={shareLink}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Share'}</Button>
        </div>
      </div>
    </div>
  );
};
